/**
 * Reading an uploaded solution in the browser: `EX-EDR03`, Wave 23 decisions 2
 * and 3 (the canvas boards Checks and Mismatch).
 *
 * Everything here runs on the contestant's own machine. Nothing leaves it until
 * a submit, and then only the text, through the same route a typed solution
 * takes. The checks stop the mistakes that cost a penalty or a wasted attempt
 * (a compiled `.exe`, a 60,000-character file the server would refuse, a `.py`
 * sent as C++, a Java class the judge cannot run) before the file reaches the
 * editor, never after.
 *
 * Pure apart from reading the `Blob`, so each rule has a unit test on a real
 * `File`.
 */
import { PLAYGROUND_LANGUAGE_PRESENTATION } from "@/features/playground/config/languages";
import { getLanguagePolicyById } from "@/features/playground/config/backend-language-contract.generated";
import { CODING_SOURCE_CODE_MAX } from "@/features/quiz/coding-task-api";
import { seedRuntime, type AllowedRuntime } from "../components/workspace/use-solution-draft";

/* ── Which language a file name says ──────────────────────────────────────── */

/**
 * Names a language's source file may carry beyond its main one. The main one is
 * the Playground's `fileExtension`, which is the runner's own `sourceFile` name
 * (`progsity-runner/src/languages/language-contract.ts`: `main.cpp`, `Main.java`
 * and so on); C++ is also saved as `.cc` and `.cxx` by the IDEs students use.
 */
const EXTRA_EXTENSIONS: Readonly<Record<string, readonly string[]>> = {
  cpp: [".cc", ".cxx"],
};

/** Every source extension this platform reads, to the language it names. */
export const SOURCE_FILE_LANGUAGE_BY_EXTENSION: ReadonlyMap<string, string> = new Map(
  PLAYGROUND_LANGUAGE_PRESENTATION.flatMap((language) =>
    [language.fileExtension, ...(EXTRA_EXTENSIONS[language.id] ?? [])].map(
      (extension) => [extension.toLowerCase(), language.id] as const,
    ),
  ),
);

/** A language's source extensions, its main one first (`.cpp`, `.cc`, `.cxx`). */
export function sourceExtensionsFor(languageKey: string): string[] {
  return [...SOURCE_FILE_LANGUAGE_BY_EXTENSION.entries()]
    .filter(([, id]) => id === languageKey)
    .map(([extension]) => extension);
}

/** `.cpp` for `pairs.CPP`; `null` for a name with no extension at all. */
export function fileExtensionOf(name: string): string | null {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return null;
  return name.slice(dot).toLowerCase();
}

/**
 * The language a file name says, or `null` when it says none we know (`.txt`,
 * no extension). `null` is "not checked", never a refusal: a solution saved as
 * `solution.txt` is still a solution.
 */
export function languageForFileName(name: string): string | null {
  const extension = fileExtensionOf(name);
  return extension ? (SOURCE_FILE_LANGUAGE_BY_EXTENSION.get(extension) ?? null) : null;
}

/**
 * The runtime "Use C++17" switches to: the editor's own when it is already that
 * language, else the version the contract defaults to (`preferredJudgeRuntime`'s
 * rule), else any version this round offers. `null` when the round offers none.
 */
export function runtimeForLanguage(
  allowedRuntimes: readonly AllowedRuntime[],
  languageKey: string,
  current: AllowedRuntime | null,
): AllowedRuntime | null {
  if (current?.languageKey === languageKey) return current;
  const offered = allowedRuntimes.filter((runtime) => runtime.languageKey === languageKey);
  if (offered.length === 0) return null;
  const version = getLanguagePolicyById(languageKey)?.defaultVersionId.runner;
  return seedRuntime(offered, version ? { languageKey, languageVersion: version } : null) ?? null;
}

/* ── Reading the file ─────────────────────────────────────────────────────── */

/**
 * Files larger than this are refused by their size without being read whole.
 * Fifty thousand characters are at most 200 KB of UTF-8, so anything past a
 * megabyte is too long whatever it holds, and reading a 2 GB video someone
 * dropped by mistake would freeze the tab.
 */
export const SOURCE_FILE_READ_MAX_BYTES = 1024 * 1024;

/** How much of an oversized file is read to tell "binary" from "too long". */
const SNIFF_BYTES = 8 * 1024;

export type SourceFileRefusal =
  | { reason: "empty" }
  | { reason: "binary" }
  /** `chars` is `null` when the file was too large to read and only its size is known. */
  | { reason: "tooLong"; chars: number | null; bytes: number }
  | { reason: "unreadable" };

export interface SourceFileText {
  /** UTF-8, no byte-order mark, every line ending `\n`. What the editor receives. */
  text: string;
  /** The file's own size on disk. */
  bytes: number;
  lines: number;
  /** `text.length`, the unit the editor's counter and the server's bound use. */
  chars: number;
  /** True when Windows (`\r\n`) or old Mac (`\r`) line endings were turned into `\n`. */
  lineEndingsFixed: boolean;
}

export type SourceFileResult = { ok: true; file: SourceFileText } | { ok: false; refusal: SourceFileRefusal };

/**
 * A NUL byte, or bytes that are not UTF-8.
 *
 * `File.text()` is deliberately not used: it replaces bad bytes with U+FFFD and
 * never fails, so a compiled program would arrive in the editor as a screen of
 * question marks. A fatal decoder refuses it instead.
 */
function decodeText(bytes: Uint8Array): string | null {
  if (bytes.includes(0)) return null;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** Lines as an editor numbers them: a final newline does not start another. */
export function countLines(text: string): number {
  if (text.length === 0) return 0;
  const breaks = text.split("\n").length - 1;
  return text.endsWith("\n") ? breaks : breaks + 1;
}

export async function readSourceFile(file: Blob): Promise<SourceFileResult> {
  try {
    if (file.size > SOURCE_FILE_READ_MAX_BYTES) {
      const head = new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer());
      // The sniff may cut a multi-byte character in half at its end, so only the
      // NUL byte decides here; a text file this large is too long either way.
      if (head.includes(0)) return { ok: false, refusal: { reason: "binary" } };
      return { ok: false, refusal: { reason: "tooLong", chars: null, bytes: file.size } };
    }

    const decoded = decodeText(new Uint8Array(await file.arrayBuffer()));
    if (decoded === null) return { ok: false, refusal: { reason: "binary" } };

    // `TextDecoder` already drops a leading byte-order mark; this is the belt.
    const withoutBom = decoded.replace(/^﻿/, "");
    const text = withoutBom.replace(/\r\n?/g, "\n");
    if (text.trim().length === 0) return { ok: false, refusal: { reason: "empty" } };
    if (text.length > CODING_SOURCE_CODE_MAX) {
      return { ok: false, refusal: { reason: "tooLong", chars: text.length, bytes: file.size } };
    }

    return {
      ok: true,
      file: {
        text,
        bytes: file.size,
        lines: countLines(text),
        chars: text.length,
        lineEndingsFixed: text !== withoutBom,
      },
    };
  } catch {
    return { ok: false, refusal: { reason: "unreadable" } };
  }
}

/* ── Java's class name ────────────────────────────────────────────────────── */

/**
 * The source with every comment, string and character literal blanked to
 * spaces, offsets and newlines kept, so a `public class Solution` inside a
 * comment or a string is never mistaken for the declaration.
 */
function maskJavaNonCode(source: string): string {
  const out = source.split("");
  const blank = (from: number, to: number) => {
    for (let index = from; index < to; index += 1) {
      if (out[index] !== "\n") out[index] = " ";
    }
  };

  let index = 0;
  while (index < source.length) {
    const char = source[index];
    const next = source[index + 1];
    if (char === "/" && next === "/") {
      const end = source.indexOf("\n", index);
      const stop = end === -1 ? source.length : end;
      blank(index, stop);
      index = stop;
    } else if (char === "/" && next === "*") {
      const end = source.indexOf("*/", index + 2);
      const stop = end === -1 ? source.length : end + 2;
      blank(index, stop);
      index = stop;
    } else if (source.startsWith('"""', index)) {
      const end = source.indexOf('"""', index + 3);
      const stop = end === -1 ? source.length : end + 3;
      blank(index, stop);
      index = stop;
    } else if (char === '"' || char === "'") {
      let cursor = index + 1;
      while (cursor < source.length && source[cursor] !== char && source[cursor] !== "\n") {
        cursor += source[cursor] === "\\" ? 2 : 1;
      }
      const stop = Math.min(source.length, cursor + 1);
      blank(index, stop);
      index = stop;
    } else {
      index += 1;
    }
  }
  return out.join("");
}

const PUBLIC_CLASS = /\bpublic\s+(?:(?:final|abstract|static|strictfp)\s+)*class\s+([A-Za-z_$][\w$]*)/;

/**
 * The first top-level `public class` the source declares, with the offset of its
 * name, or `null` when there is none. The runner compiles every Java solution as
 * `Main.java` and runs `Main`, so a `public class Solution` is a compilation
 * error waiting to happen.
 */
export function javaPublicClass(source: string): { name: string; index: number } | null {
  const match = PUBLIC_CLASS.exec(maskJavaNonCode(source));
  if (!match?.[1]) return null;
  return { name: match[1], index: match.index + match[0].length - match[1].length };
}

/**
 * Renames that one declaration to `Main` and touches nothing else: a
 * constructor or a `Solution.helper()` call elsewhere is the contestant's to
 * see, and quietly rewriting their code would be worse than a compile message.
 */
export function renameJavaClassToMain(source: string): string {
  const found = javaPublicClass(source);
  if (!found || found.name === "Main") return source;
  return source.slice(0, found.index) + "Main" + source.slice(found.index + found.name.length);
}
